// @ts-ignore - allow import with query string
import { lockBody, fetchModalContent } from './util.js?v=25';

if (!customElements.get('site-modal')) {
  customElements.define(
    'site-modal',
    class Modal extends HTMLElement {
      CLASSES = {
        modalOpenClass: `site-modal-is-open--${this.key}`,
      };

      constructor() {
        super();

        this.elements.triggers.forEach((trigger) => {
          trigger.addEventListener('click', this.handleTrigger.bind(this));
        });

        this.initCloseTriggers();
        this.handleOpenOnInit();

        this.isOpen && this.handleOpen();

        document.addEventListener('keyup', this.handleKeyup.bind(this));

        document.addEventListener('openModal', this.handleOpen.bind(this));
      }

      connectedCallback() {
        window.addEventListener('popstate', this.handlePopState.bind(this));
      }

      disconnectedCallback() {
        window.removeEventListener('popstate', this.handlePopState.bind(this));
      }

      handleOpenOnInit() {
        const params = new URLSearchParams(window.location.search);

        if (params.has('open-modal') && params.get('open-modal') === this.key) {
          /**
           * @type {HTMLElement | null}
           */
          const trigger = document.querySelector(`button[js-${this.key}-trigger]`);

          if (trigger && this.key) {
            fetchModalContent(trigger, this.key);

            const url = new URL(window.location.href);

            url.searchParams.delete('open-modal');
            window.history.replaceState(null, '', url);
          }
        }
      }

      /**
       * @param {CustomEvent} [event]
       */
      handleOpen(event) {
        if (event && event.detail.key != this.key) return;

        this.isFullscreen && lockBody(true);
        this.setAttribute('aria-hidden', 'false');
        this.body.classList.add(this.CLASSES.modalOpenClass);
        this.initCloseTriggers();
        this.focus();

        setTimeout(() => {
          document.documentElement.classList.remove('open-modal');
        }, 300);

        /*
         * Push history state for fullscreen modals
         */
        if (this.isFullscreen && !this.redirectOnClose) {
          this.setModalOpenQuery();
        }
      }

      handleClose() {
        if (!this.isOpen) return;

        if (this.isFullscreen && !this.redirectOnClose) {
          this.deleteModalOpenQuery();
        }

        if (this.redirectOnClose) {
          this.handleRedirect();
        } else {
          lockBody(false);
          this.setAttribute('aria-hidden', 'true');
          this.body.classList.remove(this.CLASSES.modalOpenClass);
          this.blur();
          this.dispatchEvent(new CustomEvent('closeModal', { detail: { key: this.key } }));
        }
      }

      setModalOpenQuery() {
        if (!this.key) return;

        const url = new URL(window.location.href);

        url.searchParams.set('modal-open', this.key);
        window.history.pushState({}, '', url);
      }

      deleteModalOpenQuery() {
        if (!window.location.search.includes('modal-open') || !this.isFullscreen) return;

        const url = new URL(window.location.href);
        url.searchParams.delete('modal-open');
        window.history.back();
      }

      handleRedirect() {
        if (document.referrer.includes(window.location.hostname)) {
          window.history.back();
        } else {
          window.location.href = '/';
        }
      }

      handlePopState() {
        // if (!this.isFullscreen) return;

        this.handleClose();
      }

      /**
       * @param {MouseEvent} event
       */
      handleTrigger(event) {
        event.preventDefault();

        this.isOpen ? this.handleClose() : this.handleOpen();
      }

      /**
       * @param {MouseEvent} event
       */
      handleClick(event) {
        if (this.isFullscreen || !event.target) return;

        /**
         * @type {HTMLElement}
         */
        const target = event.target;

        if (!target.closest('.site-modal') && !target.closest('[js-modal-trigger]')) {
          this.handleClose();
        }
      }

      /**
       * @param {KeyboardEvent} event
       */
      handleKeyup(event) {
        switch (event.key) {
          case 'Escape':
            this.handleClose();
          default:
            break;
        }
      }

      initCloseTriggers() {
        this.elements.close.forEach((close) => {
          close.addEventListener('click', this.handleClose.bind(this));
        });
      }

      get isOpen() {
        return this.getAttribute('aria-hidden') === 'false';
      }

      get key() {
        return this.getAttribute('key');
      }

      get elements() {
        return {
          /**
           * @type {NodeListOf<HTMLElement>}
           */
          triggers: document.querySelectorAll(`[js-modal-trigger=${this.key}]`),
          close: this.querySelectorAll(`[js-modal-close]`),
        };
      }

      get content() {
        return this.querySelector('.site-modal__content');
      }

      get body() {
        return document.body;
      }

      get isFullscreen() {
        return this.dataset.type === 'fullscreen';
      }

      get redirectOnClose() {
        return this.dataset.redirectOnClose === 'true';
      }
    },
  );
}
